export default `in vec4 v_component_coords;

uniform sampler2D u_tex;

uniform vec4 u_tex_rect;

uniform bvec2 u_wrap_component;

uniform vec2 u_wrap_dimensions;

uniform vec2 u_bin_size;

bool circle_filled(vec2 coords, vec2 circle_pos, float circle_radius) {
    vec2 from_center = coords - circle_pos;
    float center_dist_sqr = from_center.x * from_center.x + from_center.y * from_center.y;
    return center_dist_sqr < circle_radius * circle_radius;
}

bool circle(vec2 coords, vec2 circle_pos, float circle_radius, float thickness) {
    vec2 from_center = coords - circle_pos;
    float center_dist_sqr = from_center.x * from_center.x + from_center.y * from_center.y;
    return center_dist_sqr < (circle_radius + thickness) * (circle_radius + thickness) &&
    center_dist_sqr > (circle_radius - thickness) * (circle_radius - thickness);
}

bool quarter_circle(vec2 coords, vec2 circle_pos, float circle_radius, float thickness,
bool topLeft, bool topRight, bool bottomRight, bool bottomLeft) {
    bool isLeft = coords.x <= circle_pos.x;
    bool isTop = coords.y >= circle_pos.y;
    return (topLeft && isLeft && isTop
    || topRight && !isLeft && isTop
    || bottomRight && !isLeft && !isTop
    || bottomLeft && isLeft && !isTop)
    && circle(coords, circle_pos, circle_radius, thickness);
}

bool line_h(vec2 coords, vec2 start, vec2 end, float thickness) {
    float slope = (end.y - start.y) / (end.x - start.x);
    if (coords.x < start.x || coords.x > end.x) return false;
    float line_y = start.y + slope * (coords.x - start.x);
    return (coords.y <= line_y + thickness && coords.y >= line_y - thickness);
}

bool line_v(vec2 coords, vec2 start, vec2 end, float thickness) {
    float slope = (end.x - start.x) / (end.y - start.y);
    if (coords.y < start.y || coords.y > end.y) return false;
    float line_x = start.x + slope * (coords.y - start.y);
    return (coords.x <= line_x + thickness && coords.x >= line_x - thickness);
}

bool ellipse(vec2 coords, vec2 pos, vec2 radii, float thickness) {
    vec2 from_center = (coords - pos) / radii;
    float center_dist_sqr = (from_center.x * from_center.x + from_center.y * from_center.y);
    return center_dist_sqr < (1. + thickness / radii.x) * (1. + thickness / radii.y) &&
    center_dist_sqr > (1. - thickness / radii.x) * (1. - thickness / radii.y);
}

bool quarter_ellipse(vec2 coords, vec2 pos, vec2 radii, float thickness,
bool topLeft, bool topRight, bool bottomRight, bool bottomLeft) {
    bool isLeft = coords.x <= pos.x;
    bool isTop = coords.y >= pos.y;
    if (!(topLeft && isLeft && isTop
    || topRight && !isLeft && isTop
    || bottomRight && !isLeft && !isTop
    || bottomLeft && isLeft && !isTop)) return false;
    vec2 from_center = (coords - pos) / radii;
    float center_dist_sqr = (from_center.x * from_center.x + from_center.y * from_center.y);
    return center_dist_sqr < (1. + thickness / radii.x) * (1. + thickness / radii.y) &&
    center_dist_sqr > (1. - thickness / radii.x) * (1. - thickness / radii.y);
}

bool rect(vec2 coords, vec4 rect_bounds) {
    return coords.x > rect_bounds.x && coords.x < rect_bounds.z
    && coords.y > rect_bounds.y && coords.y < rect_bounds.w;
}

bool vertical_line(vec2 coords, float x, float bottom, float height, float thickness) {
    return rect(coords, vec4(x - thickness, bottom, x + thickness, bottom + height));
}

bool horizontal_line(vec2 coords, float left, float width, float y, float thickness) {
    return rect(coords, vec4(left, y - thickness, left + width, y + thickness));
}

bool number_0(vec2 coords, vec2 baseline, float font) {
    return ellipse(coords, baseline + vec2(0, font), vec2(font * .5, font * .9), font * .07)
    || line_h(coords, vec2(baseline.x - font * .25, baseline.y + font * .25), vec2(baseline.x + font * .25, baseline.y + font * 1.75), font * .2);
}

bool number_1(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x, baseline.y, font * 1.9, font * .07)
    || horizontal_line(coords, baseline.x - font * .4, font * .75, baseline.y, font * .07)
    || line_h(coords, vec2(baseline.x - font * .35, baseline.y + font * 1.65), vec2(baseline.x + font * .05, baseline.y + font * 1.87), font * .08);
}

bool number_2(vec2 coords, vec2 baseline, float font) {
    return quarter_circle(coords, baseline + vec2(0, font * 1.6), font * .4, font * .07, true, true, false, false)
    || quarter_ellipse(coords, baseline + vec2(-font * .43, font * 1.6), vec2(font * 0.85, font * 1.56), font * .08, false, false, true, false)
    || horizontal_line(coords, baseline.x - font * .45, font * 1., baseline.y, font * .07);
}

bool number_3(vec2 coords, vec2 baseline, float font) {
    return (circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07)
    && !circle_filled(coords, baseline + vec2(-font * .5, font * .75), font * .5))
    || (circle(coords, baseline + vec2(0, font * 1.5), font * .5, font * .07)
    && !circle_filled(coords, baseline + vec2(-font * .5, font * 1.25), font * .5));
}

bool number_4(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x + font * .25, baseline.y, font * 1.9, font * .07)
    || horizontal_line(coords, baseline.x - font * .4, font, baseline.y + font, font * .07)
    || line_v(coords, vec2(baseline.x - font * .35, baseline.y + font), vec2(baseline.x - font * .2, baseline.y + font * 1.9), font * .07);
}

bool number_5(vec2 coords, vec2 baseline, float font) {
    return (circle(coords, baseline + vec2(0, font * .55), font * .55, font * .07)
    && !circle_filled(coords, baseline + vec2(-font * .7, font * .75), font * .5))
    || vertical_line(coords, baseline.x - font * .3, baseline.y + font * .95, font * .9, font * .07)
    || horizontal_line(coords, baseline.x - font * .37, font * .9, baseline.y + font * 1.9, font * .07);
}

bool number_6(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .55), font * .55, font * .07)
    || quarter_ellipse(coords, baseline + vec2(font * .25, font * .7), vec2(font * .8, font * 1.2), font * .07, true, false, false, false);
}

bool number_7(vec2 coords, vec2 baseline, float font) {
    return horizontal_line(coords, baseline.x - font * .5, font, baseline.y + font * 1.85, font * .07)
    || line_v(coords, vec2(baseline.x - font * .25, baseline.y), vec2(baseline.x + font * .45, baseline.y + font * 1.9), font * .07);
}

bool number_8(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07)
    || circle(coords, baseline + vec2(0, font * 1.5), font * .5, font * .07);
}

bool number_9(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x + font * .5, baseline.y, font * 1.7, font * .07)
    || circle(coords, baseline + vec2(0, font * 1.5), font * .5, font * .07);
}

bool symbol_period(vec2 coords, vec2 baseline, float font) {
    return circle_filled(coords, baseline + vec2(0, font * .2), font * .15);
}

bool symbol_dash(vec2 coords, vec2 baseline, float font) {
    return horizontal_line(coords, baseline.x - font * .4, font * .8, baseline.y + font, font * .07);
}

bool symbol_percent(vec2 coords, vec2 baseline, float font) {
    return line_v(coords, vec2(baseline.x - font * .375, baseline.y + font * .25), vec2(baseline.x + font * .375, baseline.y + font * 1.75), font * .07)
    || circle(coords, baseline + vec2(-font * .25, font * 1.5), font * .25, font * .07)
    || circle(coords, baseline + vec2(font * .25, font * .5), font * .25, font * .07);
}

bool uppercase_o(vec2 coords, vec2 baseline, float font) {
    return ellipse(coords, baseline + vec2(0, font), vec2(font * .5, font * .9), font * .07);
}

bool lowercase_a(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07)
    || rect(coords, vec4(baseline.x + font * .5, baseline.y, baseline.x + font * .5 + font * .125, baseline.y + font));
}

bool lowercase_b(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07)
    || vertical_line(coords, baseline.x - font * .5, baseline.y, font * 2., font * .07);
}

bool lowercase_c(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07)
    && !circle_filled(coords, baseline + font * .5, font * .3);
}

bool lowercase_d(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07)
    || vertical_line(coords, baseline.x + font * .5, baseline.y, font * 2., font * .07);
}

bool lowercase_e(vec2 coords, vec2 baseline, float font) {
    return ellipse(coords, baseline + vec2(0, font * .65), vec2(font * .46, font * .3), font * .07)
    || (ellipse(coords, baseline + vec2(font * .1, font * .5), vec2(font * .6, font * .5), font * .07)
    && !circle_filled(coords, baseline + vec2(font * .3, font * .72), font * .65));
}

bool lowercase_f(vec2 coords, vec2 baseline, float font) {
    return quarter_circle(coords, baseline + vec2(font * .1, font * 1.5), font * .35, font * .07, true, true, false, false)
    || vertical_line(coords, baseline.x - font * .25, baseline.y, font * 1.6, font * .07)
    || horizontal_line(coords, baseline.x - font * .5, font * .75, baseline.y + font, font * .07);
}

bool lowercase_g(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07)
    || vertical_line(coords, baseline.x + font * .5, baseline.y - font * .5, font * 1.5, font * .07)
    || quarter_circle(coords, baseline + vec2(0, -font * .45), font * .5, font * .07, false, false, true, true);
}

bool lowercase_h(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x - font * .5, baseline.y, font * 2., font * .07)
    || quarter_circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07, true, true, false, false)
    || vertical_line(coords, baseline.x + font * .5, baseline.y, font * .5, font * .07);
}

bool lowercase_i(vec2 coords, vec2 baseline, float font) {
    return circle_filled(coords, baseline + vec2(0, font * 1.35), font * .1)
    || vertical_line(coords, baseline.x, baseline.y, font, font * .07);
}

bool lowercase_j(vec2 coords, vec2 baseline, float font) {
    return circle_filled(coords, baseline + vec2(0, font * 1.35), font * .1)
    || vertical_line(coords, baseline.x, baseline.y - font * .5, font * 1.5, font * .07)
    || quarter_circle(coords, baseline + vec2(-font * .2, -font * .45), font * .2, font * .07, false, false, true, true);
}

bool lowercase_k(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x - font * .25, baseline.y, font * 1.5, font * .07)
    || line_h(coords, vec2(baseline.x - font * .25, baseline.y + font * .5), vec2(baseline.x + font * .25, baseline.y + font), font * .1)
    || line_h(coords, vec2(baseline.x - font * .25, baseline.y + font * .5), vec2(baseline.x + font * .25, baseline.y + font * .06), font * .12);
}

bool lowercase_l(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x, baseline.y, font * 1.5, font * .07);
}

bool lowercase_m(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x - font * .5, baseline.y, font, font * .07)
    || quarter_circle(coords, baseline + vec2(-font * .25, font * .65), font * .25, font * .07, true, true, false, false)
    || vertical_line(coords, baseline.x, baseline.y, font * .65, font * .07)
    || quarter_circle(coords, baseline + vec2(font * .25, font * .65), font * .25, font * .07, true, true, false, false)
    || vertical_line(coords, baseline.x + font * .5, baseline.y, font * .65, font * .07);
}

bool lowercase_n(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x - font * .5, baseline.y, font, font * .07)
    || quarter_circle(coords, baseline + vec2(0, font * .45), font * .5, font * .07, true, true, false, false)
    || vertical_line(coords, baseline.x + font * .5, baseline.y, font * .5, font * .07);
}

bool lowercase_o(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07);
}

bool lowercase_p(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07)
    || vertical_line(coords, baseline.x - font * .5, baseline.y - font, font * 2., font * .07);
}

bool lowercase_q(vec2 coords, vec2 baseline, float font) {
    return circle(coords, baseline + vec2(-font * .15, font * .45), font * .4, font * .07)
    || vertical_line(coords, baseline.x + font * .3, baseline.y - font, font * 1.9, font * .07)
    || line_h(coords, vec2(baseline.x + font * .25, baseline.y - font), vec2(baseline.x + font * .75, baseline.y - font * .5), font * .1);
}

bool lowercase_r(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x - font * .5, baseline.y, font, font * .07)
    || quarter_circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07, true, true, false, false);
}

bool lowercase_s(vec2 coords, vec2 baseline, float font) {
    return quarter_circle(coords, baseline + vec2(0, font * .3), font * .25, font * .07, false, true, true, true)
    || quarter_circle(coords, baseline + vec2(0, font * .81), font * .25, font * .07, true, true, false, true);
}

bool lowercase_t(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x - font * .25, baseline.y + font * .2, font * 1.4, font * .07)
    || horizontal_line(coords, baseline.x - font * .5, font * .75, baseline.y + font, font * .07)
    || quarter_circle(coords, baseline + vec2(font * .1, font * .25), font * .35, font * .07, false, false, false, true);
}

bool lowercase_u(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x - font * .5, baseline.y + font * .5, font * .5, font * .07)
    || quarter_circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07, false, false, true, true)
    || vertical_line(coords, baseline.x + font * .5, baseline.y, font, font * .07);
}

bool lowercase_v(vec2 coords, vec2 baseline, float font) {
    return line_v(coords, baseline, vec2(baseline.x - font * .45, baseline.y + font), font * .07)
    || line_v(coords, baseline, vec2(baseline.x + font * .45, baseline.y + font), font * .07);
}

bool lowercase_x(vec2 coords, vec2 baseline, float font) {
    return line_v(coords, vec2(baseline.x + font * .5, baseline.y), vec2(baseline.x - font * .45, baseline.y + font), font * .09)
    || line_v(coords, vec2(baseline.x - font * .5, baseline.y), vec2(baseline.x + font * .45, baseline.y + font), font * .09);
}

bool lowercase_y(vec2 coords, vec2 baseline, float font) {
    return vertical_line(coords, baseline.x - font * .5, baseline.y + font * .5, font * .5, font * .07)
    || quarter_circle(coords, baseline + vec2(0, font * .5), font * .5, font * .07, false, false, true, true)
    || vertical_line(coords, baseline.x + font * .5, baseline.y - font * .5, font * 1.5, font * .07)
    || quarter_circle(coords, baseline + vec2(0, -font * .45), font * .5, font * .07, false, false, true, true);
}

vec2 baseline_from_index(int x, int y, float font) {
    float line_height = font * 2.5;
    return vec2(font * 1.25 * (float(x) + .5), line_height * (float(y) + .02));
}

vec2 coord_to_index(vec2 coord, float font) {
    float line_height = font * 2.5;
    return vec2(coord.x / font / 1.25, coord.y / line_height - .02);
}

float render_character(int c, vec2 coords, vec2 baseline, float font) {
    if (c == 32) {  }
    else if (c == 37 && symbol_percent(coords, baseline, font)) return 1.0;
    else if (c == 45 && symbol_dash(coords, baseline, font)) return 1.0;
    else if (c == 46 && symbol_period(coords, baseline, font)) return 1.0;

    else if (c == 48 && number_0(coords, baseline, font)) return 1.0;
    else if (c == 49 && number_1(coords, baseline, font)) return 1.0;
    else if (c == 50 && number_2(coords, baseline, font)) return 1.0;
    else if (c == 51 && number_3(coords, baseline, font)) return 1.0;
    else if (c == 52 && number_4(coords, baseline, font)) return 1.0;
    else if (c == 53 && number_5(coords, baseline, font)) return 1.0;
    else if (c == 54 && number_6(coords, baseline, font)) return 1.0;
    else if (c == 55 && number_7(coords, baseline, font)) return 1.0;
    else if (c == 56 && number_8(coords, baseline, font)) return 1.0;
    else if (c == 57 && number_9(coords, baseline, font)) return 1.0;

    else if (c == 79 && uppercase_o(coords, baseline, font)) return 1.0;

    else if (c == 97 && lowercase_a(coords, baseline, font)) return 1.0;
    else if (c == 98 && lowercase_b(coords, baseline, font)) return 1.0;
    else if (c == 99 && lowercase_c(coords, baseline, font)) return 1.0;
    else if (c == 100 && lowercase_d(coords, baseline, font)) return 1.0;
    else if (c == 101 && lowercase_e(coords, baseline, font)) return 1.0;
    else if (c == 102 && lowercase_f(coords, baseline, font)) return 1.0;
    else if (c == 103 && lowercase_g(coords, baseline, font)) return 1.0;
    else if (c == 104 && lowercase_h(coords, baseline, font)) return 1.0;
    else if (c == 105 && lowercase_i(coords, baseline, font)) return 1.0;
    else if (c == 106 && lowercase_j(coords, baseline, font)) return 1.0;
    else if (c == 107 && lowercase_k(coords, baseline, font)) return 1.0;
    else if (c == 108 && lowercase_l(coords, baseline, font)) return 1.0;
    else if (c == 109 && lowercase_m(coords, baseline, font)) return 1.0;
    else if (c == 110 && lowercase_n(coords, baseline, font)) return 1.0;
    else if (c == 111 && lowercase_o(coords, baseline, font)) return 1.0;
    else if (c == 112 && lowercase_p(coords, baseline, font)) return 1.0;
    else if (c == 113 && lowercase_q(coords, baseline, font)) return 1.0;
    else if (c == 114 && lowercase_r(coords, baseline, font)) return 1.0;
    else if (c == 115 && lowercase_s(coords, baseline, font)) return 1.0;
    else if (c == 116 && lowercase_t(coords, baseline, font)) return 1.0;
    else if (c == 117 && lowercase_u(coords, baseline, font)) return 1.0;
    else if (c == 118 && lowercase_v(coords, baseline, font)) return 1.0;
    
    else if (c == 120 && lowercase_x(coords, baseline, font)) return 1.0;
    else if (c == 121 && lowercase_y(coords, baseline, font)) return 1.0;
    

    return 0.;
}

int to_ascii(int digit) {
    if (digit < 0 || digit > 9) return 120;
    return digit + 48;
}

int maybe_carryover_digit(inout int chars[10], inout int index, inout int digit) {
    
    int ret_value = -1;
    if (digit > 9) {
        if (index == 0) {
            for (int i = 8; i >= 0; i--) {
                chars[i+1] = chars[i];
            }
            chars[0] = 32;
            index++;
        }
        int _previousIndex = index - 1;
        
        if (chars[_previousIndex] == 46) {
            _previousIndex -= 1;
            if (_previousIndex < 0) {
                _previousIndex = 0;
                chars[1] = 46;
                index++;
            }
        }
        if (chars[_previousIndex] == 57) {
            ret_value = _previousIndex;
        }
        if (chars[_previousIndex] == 46 || chars[_previousIndex] == 32) {
            chars[_previousIndex] = 49;
        } else {
            chars[_previousIndex]++;
        }
        digit -= 10;
    }
    return ret_value;
}

int[10] double_to_chars_limit_decimal(highp float number, int num_decimals) {
    int index_max = 9;
    if (number >= 10000000000.) {
        return int[10](116, 111, 111, 32, 98, 105, 103, 32, 32, 32);
    } else if (number < -999999999.) {
        return int[10](116, 111, 111, 32, 115, 109, 97, 108, 108, 32);
    }
    int chars[10] = int[](32, 32, 32, 32, 32, 32, 32, 32, 32, 32);
    int index = 0;
    if (number < 0.) {
        chars[0] = 45;
        index++;
    }
    number = abs(number);

    bool decimal = false;
    int digits = 0;
    highp float check = number;
    while (check >= 1.) {
        digits++;
        check /= 10.;
    }

    while (index <= index_max) {
        check = number;
        if (digits > 0) {
            int mult = 1;
            for (int i = 0; i < digits - 1; i++) {
                mult *= 10;
                check /= 10.;
            }
            int digit = int(floor(check));
            chars[index] = to_ascii(digit);
            number -= float(digit * mult);
            digits--;
        } else if (check < 1.) {
            if (!decimal) {
                decimal = true;
                chars[index] = 46;
            } else {
                check *= 10.;
                int digit = int(floor(check));
                if (num_decimals == 1 || index == index_max) {
                    digit = int(round(check));

                    int _nextUpdate = maybe_carryover_digit(chars, index, digit);
                    while (_nextUpdate >= 0) {
                        int temp_digit = chars[_nextUpdate] - 48;
                        int _nextUpdate2 = maybe_carryover_digit(chars, _nextUpdate, temp_digit);
                        chars[_nextUpdate] = 48;
                        _nextUpdate = _nextUpdate2;
                    }
                }
                chars[index] = to_ascii(digit);
                number = number * 10. - float(digit);
                num_decimals--;

                if (num_decimals == 0) return chars;
            }
        }
        index++;
    }
    return chars;
}

int[10] double_to_chars(highp float number) {
    return double_to_chars_limit_decimal(number, 9);
}

float render_10x5_string(vec2 coords, int[50] strings) {
    int lines = 5;
    int string_length = 10;
    float font = .08;
    bool show_grid = false;

    vec2 index = coord_to_index(coords, font);
    vec2 index_min = index;
    index_min.x = min(max(0., index_min.x), float(string_length - 1));
    index_min.y = min(max(0., float(lines) - index_min.y - 1.), float(lines - 1));

    vec2 index_max = vec2(min(index_min.x, float(string_length - 1)),
    min(index_min.y + 1., float(lines - 1)));

    for (int x = int(index_min.x); x <= int(index_max.x); x++) {
        for (int y = int(index_min.y); y <= int(index_max.y); y++) {
            int string_index = y * string_length + x;
            int c = strings[string_index];
            vec2 baseline = baseline_from_index(x, lines - y - 1, font);

            float char_value = render_character(c, coords, baseline, font);
            if (char_value > 0.01) return char_value;
        }
    }

    if (show_grid) {
        if (abs(index.x - round(index.x)) < .01
        || abs(index.y - round(index.y)) < .01) return .25;
        if (abs(index.y + .5 - round(index.y + .5)) < .01) return .08;
    }

    return .0;
}

float render_6x3_string(vec2 coords, int[18] strings) {
    int lines = 3;
    int string_length = 6;
    float font = .133;
    bool show_grid = false;

    vec2 index = coord_to_index(coords, font);
    vec2 index_min = index;
    index_min.x = min(max(0., index_min.x), float(string_length - 1));
    index_min.y = min(max(0., float(lines) - index_min.y - 1.), float(lines - 1));

    vec2 index_max = vec2(min(index_min.x, float(string_length - 1)),
    min(index_min.y + 1., float(lines - 1)));

    for (int x = int(index_min.x); x <= int(index_max.x); x++) {
        for (int y = int(index_min.y); y <= int(index_max.y); y++) {
            int string_index = y * string_length + x;
            int c = strings[string_index];
            vec2 baseline = baseline_from_index(x, lines - y - 1, font);

            float char_value = render_character(c, coords, baseline, font);
            if (char_value > 0.01) return char_value;
        }
    }

    if (show_grid) {
        if (abs(index.x - round(index.x)) < .01
        || abs(index.y - round(index.y)) < .01) return .25;
        if (abs(index.y + .5 - round(index.y + .5)) < .01) return .08;
    }

    return .0;
}

float render_difference_from_nominal_percent(vec2 coords, float value) {
    int strings[] = int[](32, 32, 32, 32, 32, 32,
    32, 32, 32, 32, 32, 32,
    32, 32, 32, 32, 32, 32);

    if (isnan(value)) return 0.;

    int line_2[10] = double_to_chars_limit_decimal(value, 1);

    int start = 6;
    int expected_last_index = 4;
    int last_index = expected_last_index;
    while (last_index > 0 && line_2[last_index] == 32) last_index--;
    start += (expected_last_index - last_index);

    for (int i = 0; i < 5; i++) {
        strings[start + i] = line_2[i];
    }
    strings[11] = 37;

    return render_6x3_string(coords, strings);
}

/**
 * A modulo function whose return value is always positive
 * @param n                 The number to mod
 * @param m                 The number to mod by
 * @return                  An always-positive modulo of n by m
 */
float euclidean_modulo(float n, float m) {
    return m <= 0.0 ? n : mod(mod(n, m) + m, m);
}

vec2 bin_coords() {
    vec2 scaled_grid = v_component_coords.xy / u_bin_size;
    vec2 inverse_grid = v_component_coords.zw / u_bin_size;

    float result_x = abs((scaled_grid.x - floor(scaled_grid.x)));
    if (u_wrap_component.x) result_x = min(result_x, abs(inverse_grid.x));

    float result_y = abs((scaled_grid.y - floor(scaled_grid.y)));
    if (u_wrap_component.y) result_y = min(result_y, abs(inverse_grid.y));

    return vec2(result_x, result_y);
}

void main() {

    vec2 coords = v_component_coords.xy;
    coords.x = euclidean_modulo(coords.x, u_wrap_dimensions.x);
    coords.y = euclidean_modulo(coords.y, u_wrap_dimensions.y);
    vec2 uv = coords.xy / (u_tex_rect.zw - u_tex_rect.xy);

    float text_color = render_difference_from_nominal_percent(bin_coords(), texture2D(u_tex, uv).r);
    if (text_color <= .0) discard;

    text_color = 1.0 - text_color;
    gl_FragColor = vec4(text_color, text_color, text_color, 1.);

}`